import { Injectable } from '@angular/core';

declare global {
  namespace google.maps {
    interface LibraryRegistry {
      maps: MapsLibrary;
      marker: MarkerLibrary;
      places: PlacesLibrary;
      geocoding: GeocodingLibrary;
      visualization: VisualizationLibrary;
      geometry: GeometryLibrary;
      routes: RoutesLibrary;
    }

    type LibraryName = keyof LibraryRegistry;
  }
}


type GoogleMapsLibName = keyof google.maps.LibraryRegistry;
type ExtendedLibName = GoogleMapsLibName | 'clusterer';

@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private loadedLibs = new Set<string>();
  private requestedModules = new Set<ExtendedLibName>();
  private initPromise?: Promise<void>;
  private clustererScriptPromise?: Promise<void>;

  init(modules: ExtendedLibName[] = ['maps']): Promise<void> {
    const newModules = modules.filter(m => !this.loadedLibs.has(m));

    newModules.forEach(m => this.requestedModules.add(m));

    const loadTasks = newModules.map((name) => {
      if (name === 'clusterer') return this.loadMarkerClustererScript();
      return this.loadLibrary(name as GoogleMapsLibName);
    });

    if (loadTasks.length === 0) return this.initPromise || Promise.resolve();

    const additionalPromise = Promise.all(loadTasks).then(() => void 0);

    this.initPromise = this.initPromise
      ? this.initPromise.then(() => additionalPromise)
      : additionalPromise;

    return this.initPromise;
  }


  loadLibrary<L extends GoogleMapsLibName>(
    lib: L
  ): Promise<google.maps.LibraryRegistry[L]> {
    if (this.loadedLibs.has(lib)) {
      return Promise.resolve((google.maps as any)[lib]) as Promise<google.maps.LibraryRegistry[L]>;
    }

    return google.maps.importLibrary(lib).then((loaded: any) => {
      this.loadedLibs.add(lib);
      return loaded as google.maps.LibraryRegistry[L];
    });
  }

  loadMarkerClustererScript(): Promise<void> {
    if ((window as any).markerClusterer?.MarkerClusterer) {
      this.loadedLibs.add('clusterer');
      return Promise.resolve();
    }

    if (!this.clustererScriptPromise) {
      this.clustererScriptPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.loadedLibs.add('clusterer');
          resolve();
        };
        script.onerror = () => reject(new Error('MarkerClusterer script failed to load.'));
        document.head.appendChild(script);
      });
    }

    return this.clustererScriptPromise;
  }

  /** Prüft, ob alle bei init() gewünschten Module geladen sind */
  isFullyReady(): boolean {
    return [...this.requestedModules].every(lib => this.loadedLibs.has(lib));
  }

  /** Prüft, ob ein einzelnes Modul geladen ist */
  isLoaded(lib: ExtendedLibName): boolean {
    return this.loadedLibs.has(lib);
  }
}
